import { A } from '@ember/array';
import Button from 'tio-reimagined/components/button';
import { Checkbox, Input, Form } from 'tio-ui/components/forms';
import { contains, or } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { hash } from '@ember/helper';
import { HStack, VStack } from 'tio-ui/components/layout';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import * as v from 'valibot';
import Component from '@glimmer/component';
import ConfirmNameModal from './confirm-name-modal';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import formatPhone from 'tio-common/helpers/format-phone';
import TioDialogDocumentAsset from 'tio-common/components/tio/dialog/document-asset';
import type { FormResultData } from 'tio-ui/components/forms';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { RegisterUser } from '../../../routes/enroll.ts';
import type ConditionModel from 'tio-common/models/condition';
import type Owner from '@ember/owner';
import type PreRegisterService from 'tio-employee/services/pre-register.ts';
import type RouterService from 'tio-employee/services/router.ts';
import type SessionService from 'tio-employee/services/session.ts';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type ZendeskService from 'tio-employee/services/zendesk.ts';
import { enrollUserRequest } from 'tio-common/requests/enrollment';
import { enrollFamilyMemberRequest } from 'tio-common/requests/enrollment';

export interface AuthRegisterEnrollFormSignature {
  Args: {
    registerUser: RegisterUser | null;
    email?: string;
    passwordMinLength: number;
  };
}

type EnrollFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  password: string;
  passwordConfirmation: string;
  familyInvite: boolean;
};

export default class AuthRegisterEnrollFormComponent extends Component<AuthRegisterEnrollFormSignature> {
  @service declare store: StoreService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare preRegister: PreRegisterService;
  @service declare zendesk: ZendeskService;
  @service declare notifications: NotificationsService;

  @tracked formData: EnrollFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    passwordConfirmation: '',
    familyInvite: false,
  };

  @tracked isAgreedTioTerms = false;
  @tracked acceptedCustomTerms: number[] = A([]);
  @tracked persistName = false;
  @tracked formValidationErrors: Record<string, string[]> = {};
  @tracked hasSubmitted = false;
  @tracked showingTermsOfUse = false;
  @tracked showingPrivacyPolicy = false;
  @tracked showingConfirmNameModal = false;
  @tracked showConditionsError = false;

  constructor(owner: Owner, args: AuthRegisterEnrollFormSignature['Args']) {
    super(owner, args);
    this.formData.email = args.email || '';
    this.formData.firstName = args.registerUser?.firstName || '';
    this.formData.lastName = args.registerUser?.lastName || '';
    this.formData.email = args.registerUser?.email || '';
    this.formData.phoneNumber = args.registerUser?.phoneNumber || '';
    this.formData.familyInvite = args.registerUser?.familyInvite || false;
  }

  get formSchema() {
    const passwordRegExp =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,64}$/;
    return v.pipe(
      v.object({
        firstName: v.pipe(v.string(), v.nonEmpty('First Name is Required')),
        lastName: v.pipe(v.string(), v.nonEmpty('Last Name is Required')),
        email: v.pipe(v.string(), v.email('Invalid Email Address')),
        phoneNumber: v.optional(
          v.union([
            v.literal(''), // allow empty string
            v.pipe(v.string(), v.regex(/^\+?[1-9]\d{9,14}$/, 'Invalid phone number format')),
          ])
        ),
        password: v.pipe(
          v.string(),
          v.regex(
            passwordRegExp,
            'Password must contain 1 upper case letter, 1 lower case letter, 1 number, and 1 special character'
          ),
          v.minLength(
            this.args.passwordMinLength,
            `Password must be at least ${this.args.passwordMinLength} characters long`
          ),
          v.maxLength(64, 'Password must not exceed 64 characters'),
          v.nonEmpty('Password is Required')
        ),
        passwordConfirmation: v.pipe(v.string(), v.nonEmpty('Password Confirmation is Required')),
      }),
      v.forward(
        v.partialCheck(
          [['password'], ['passwordConfirmation']],
          (input) => input.password === input.passwordConfirmation,
          'Passwords must match'
        ),
        ['passwordConfirmation']
      )
    );
  }

  validateField = (data: EnrollFormData) => {
    const result = v.safeParse(this.formSchema, data);

    if (result.success) {
      this.formValidationErrors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.formValidationErrors = validationErrors;
    }
  };

  onFormChange = (result: FormResultData<EnrollFormData>, event: Event) => {
    const target = event.target as HTMLInputElement | null;
    if (target?.name === 'phoneNumber') {
      this.onPhoneChange(result.data.phoneNumber as string, event as InputEvent);
    }
    this.validateField(result.data);
    this.formData = {
      ...this.formData,
      ...result.data,
    };
  };

  onFormSubmit = (result: FormResultData<EnrollFormData>) => {
    this.hasSubmitted = true;
    this.validateField(result.data);

    if (!this.isAgreed) {
      this.showConditionsError = true;
    }

    if (!this.canSubmit) {
      return;
    }

    const hasNameChanged = this.registerUserFullName && this.fullName !== this.registerUserFullName;
    if (hasNameChanged) {
      this.showingConfirmNameModal = true;
      return;
    }

    if (this.isFamilyInvite) {
      this.registerFamilyTask.perform();
    } else {
      this.registerTask.perform();
    }
  };

  get canSubmit() {
    return !Object.entries(this.formValidationErrors).length && this.isAgreed;
  }

  get isFamilyInvite() {
    return this.formData.familyInvite;
  }

  get termsOfUse() {
    return this.preRegister.termsOfUse;
  }

  get privacyPolicy() {
    return this.preRegister.privacyPolicy;
  }

  get customTerms() {
    return this.args.registerUser?.settings?.customTerms || this.preRegister.customTerms || [];
  }

  get didAcceptAllCustomTerms() {
    return this.acceptedCustomTerms.length === this.customTerms.length;
  }

  get isAgreed() {
    return this.isAgreedTioTerms && this.didAcceptAllCustomTerms;
  }

  get fullName() {
    return `${this.formData.firstName} ${this.formData.lastName}`;
  }

  get registerUserFullName() {
    return this.args.registerUser
      ? `${this.args.registerUser.firstName} ${this.args.registerUser.lastName}`
      : '';
  }

  enforceUSPhonePrefix(input: string): string {
    // Remove any non-numeric characters from the input
    const digits = input.replace(/\D/g, '');

    // Check if the cleaned number has 10 digits (add "1") or is an 11-digit starting with "1"
    if (digits.length === 10) {
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // If the input is not 10 digits (after cleaning), or 11 digits starting with "1", it's invalid
    throw new Error('Invalid phone number. Expected 10 digits or 11 digits starting with "1".');
  }

  registerTask = dropTask(async () => {
    if (!this.canSubmit) {
      return;
    }

    const query = location.search;
    const params = new URLSearchParams(query);
    const inviteCode = params.get('inviteCode');

    try {
      const enrollmentData = {
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        registrationEmail: this.formData.email,
        employeeInviteCode: inviteCode,
        password: this.formData.password,
        phoneNumber: this.formData.phoneNumber?.length
          ? this.enforceUSPhonePrefix(this.formData.phoneNumber)
          : null,
        conditionsAccepted: [
          this.termsOfUse?.id,
          this.privacyPolicy?.id,
          ...this.acceptedCustomTerms,
        ].filter((id): id is string | number => id !== undefined && id !== null),
        persistName: this.persistName,
      };

      await this.store.request(enrollUserRequest(enrollmentData));

      await this.session.authenticate(
        'authenticator:database',
        this.formData.email,
        this.formData.password,
        {
          noTransitionAfterAuth: true,
        }
      );
      if (this.session.isAuthenticated) {
        this.router.transitionTo('/dashboard');
      }
    } catch (error) {
      this.notifications.add(error?.content?.error || 'An error occurred during enrollment', {
        appearance: 'error',
      });
    }
  });

  registerFamilyTask = dropTask(async () => {
    if (!this.canSubmit) {
      return;
    }

    const query = location.search;
    const params = new URLSearchParams(query);
    const inviteCode = params.get('inviteCode');

    try {
      const enrollmentData = {
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        registrationEmail: this.formData.email,
        employeeInviteCode: inviteCode,
        password: this.formData.password,
        phoneNumber: this.formData.phoneNumber?.length
          ? this.enforceUSPhonePrefix(this.formData.phoneNumber)
          : null,
        conditionsAccepted: [
          this.termsOfUse?.id,
          this.privacyPolicy?.id,
          ...this.acceptedCustomTerms,
        ].filter((id): id is string | number => id !== undefined && id !== null),
        persistName: this.persistName,
      };

      await this.store.request(enrollFamilyMemberRequest(enrollmentData));

      await this.session.authenticate(
        'authenticator:database',
        this.formData.email,
        this.formData.password,
        {
          noTransitionAfterAuth: true,
        }
      );
      if (this.session.isAuthenticated) {
        this.router.transitionTo('/dashboard');
      }
    } catch (error) {
      this.notifications.add(error?.content?.error || 'An error occurred during enrollment', {
        appearance: 'error',
      });
    }
  });

  didChangeAcceptanceOfCustomTerm = (condition: ConditionModel) => {
    // @ts-expect-error who knows
    const index = this.acceptedCustomTerms.indexOf(condition.id);
    // Add or remove the condition ID from the list of accepted custom terms
    index === -1
      ? // @ts-expect-error who knows
        (this.acceptedCustomTerms = [...this.acceptedCustomTerms, condition.id])
      : // @ts-expect-error who knows
        (this.acceptedCustomTerms = this.acceptedCustomTerms.filter((id) => id !== condition.id));

    if (this.isAgreed) {
      this.showConditionsError = false;
    } else if (this.hasSubmitted) {
      this.showConditionsError = true;
    }
  };

  onChangeTioTerms = (isChecked: boolean) => {
    this.isAgreedTioTerms = isChecked;
    if (this.isAgreed) {
      this.showConditionsError = false;
    } else if (this.hasSubmitted) {
      this.showConditionsError = true;
    }
  };

  onPhoneChange = (value: string, event?: InputEvent) => {
    // Remove any non-numeric characters from the input
    const target = event?.target as HTMLInputElement;
    if (isNaN(Number(value))) {
      target.value = target.value.replace(/[^0-9]/g, '');
      value = target.value;
    }

    // Limit the input to 10 digits (or 11 if it starts with "1")
    if ((value.length > 10 && !value.startsWith('1')) || value.length > 11) {
      const length = value.startsWith('1') ? 11 : 10;
      target.value = target.value.slice(0, length);
      value = target.value;
    }

    this.formData.phoneNumber = value;
  };

  showConditionsDialog = (type: string, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    switch (type) {
      case 'terms':
        this.showingTermsOfUse = true;
        break;
      case 'privacy':
        this.showingPrivacyPolicy = true;
        break;
    }
  };

  onCloseConfirmNameModal = () => {
    this.showingConfirmNameModal = false;
    this.zendesk.openWebChatWindow();
  };

  submitWithPersistedName = () => {
    this.showingConfirmNameModal = false;
    this.persistName = true;

    if (this.isFamilyInvite) {
      this.registerFamilyTask.perform();
    } else {
      this.registerTask.perform();
    }
  };

  <template>
    <Form @onChange={{this.onFormChange}} @onSubmit={{this.onFormSubmit}}>
      <VStack>
        <HStack>
          <Input
            @label={{t "login.first_name"}}
            @value={{this.formData.firstName}}
            @description={{t "login.required"}}
            @errors={{errorsForValibotField "firstName" schemaErrors=this.formValidationErrors}}
            @classes={{hash base="w-full"}}
            name="firstName"
            @isRequired={{true}}
            data-test-first-name
          />
          <Input
            @label={{t "login.last_name"}}
            @value={{this.formData.lastName}}
            @description={{t "login.required"}}
            @errors={{errorsForValibotField "lastName" schemaErrors=this.formValidationErrors}}
            @classes={{hash base="w-full"}}
            name="lastName"
            @isRequired={{true}}
            data-test-last-name
          />
        </HStack>
        <HStack>
          <Input
            @label={{t "login.email"}}
            @value={{this.formData.email}}
            @description={{t "login.email_hint"}}
            @errors={{errorsForValibotField "email" schemaErrors=this.formValidationErrors}}
            @classes={{hash base="w-full"}}
            @type="email"
            name="email"
            @isRequired={{true}}
            data-test-email
          />
          <Input
            @label={{t "login.mobile"}}
            @value={{formatPhone (or this.formData.phoneNumber "")}}
            @description={{t "login.mobile_hint"}}
            @errors={{errorsForValibotField "phoneNumber" schemaErrors=this.formValidationErrors}}
            @classes={{hash base="w-full"}}
            name="phoneNumber"
            data-test-phone
          />
        </HStack>
        <Input
          @label={{t "login.create_password"}}
          @description={{t "login.minimum_characters" min=@passwordMinLength}}
          @errors={{errorsForValibotField
            "password"
            schemaErrors=this.formValidationErrors
            hasSubmitted=this.hasSubmitted
          }}
          @type="password"
          name="password"
          @isRequired={{true}}
          data-test-password
        />
        <Input
          @label={{t "login.reenter_password"}}
          @description={{t "login.minimum_characters" min=@passwordMinLength}}
          @errors={{errorsForValibotField
            "passwordConfirmation"
            schemaErrors=this.formValidationErrors
            hasSubmitted=this.hasSubmitted
          }}
          @type="password"
          name="passwordConfirmation"
          @isRequired={{true}}
          data-test-password-confirmation
        />
        <div class="flex mb-4">
          <Checkbox
            data-test-enroll-tio-conditions
            @checked={{this.isAgreedTioTerms}}
            @onChange={{this.onChangeTioTerms}}
            aria-labelledby="appTermsAndPrivacy"
          />
          <div id="appTermsAndPrivacy" class="p-0">
            {{t "login.i_have_read_and_agree_to_tuitions"}}
            <button
              class="tio-copy"
              type="button"
              {{on "click" (fn this.showConditionsDialog "terms")}}
            >
              {{t "login.terms_of_service"}}
            </button>
            {{t "and"}}
            <button
              class="tio-copy"
              type="button"
              {{on "click" (fn this.showConditionsDialog "privacy")}}
            >
              {{t "login.privacy_policy"}}.
            </button>
          </div>
        </div>
        {{#each this.customTerms as |condition|}}
          <div class="flex mb-4">
            <Checkbox
              data-test-enroll-condition
              @size="lg"
              @checked={{contains this.acceptedCustomTerms condition}}
              @onChange={{fn this.didChangeAcceptanceOfCustomTerm condition}}
              aria-labelledby={{condition.id}}
            />
            <span id={{condition.id}}>
              {{htmlSafe condition.fullText}}
            </span>
          </div>
        {{/each}}
        {{#if this.showConditionsError}}
          <div class="text-red-600 dark:text-red-400 text-sm mt-2" data-test-conditions-error>
            {{t "login.please_agree_to_all_terms"}}
          </div>
        {{/if}}
        <div class="flex">
          <Button
            @intent="primary"
            type="submit"
            @isRunning={{or this.registerTask.isRunning this.registerFamilyTask.isRunning}}
            class="my-4 mx-auto"
          >
            {{t "login.get_started"}}
          </Button>
        </div>
      </VStack>
    </Form>

    <ConfirmNameModal
      @show={{this.showingConfirmNameModal}}
      @onClose={{this.onCloseConfirmNameModal}}
      @employeeFullName={{this.registerUserFullName}}
      @inputName={{this.fullName}}
      @onSubmit={{this.submitWithPersistedName}}
    />

    <TioDialogDocumentAsset
      @title="Privacy Policy"
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.privacyPolicy.staticJsonAssetName}}
      @show={{this.showingPrivacyPolicy}}
      @onCancel={{fn (mut this.showingPrivacyPolicy) false}}
    />

    <TioDialogDocumentAsset
      @title="Terms of Service"
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.termsOfUse.staticJsonAssetName}}
      @show={{this.showingTermsOfUse}}
      @onCancel={{fn (mut this.showingTermsOfUse) false}}
    />
  </template>
}
